import React from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    DialogFooter,
} from "@shadcn-in-nx/ui/dialog";
import { Button } from "@shadcn-in-nx/ui/button";
import { Checkbox } from "@shadcn-in-nx/ui/checkbox";
import { GripVertical, Columns } from "lucide-react";
import { toast } from "sonner";

function ColumnOrganizerDialog({
    columns,
    hiddenColumns,
    columnOrder,
    onToggleVisibility,
    onReorderColumns,
}) {
    const [open, setOpen] = React.useState(false);
    const [localOrder, setLocalOrder] = React.useState([]);
    const [localHidden, setLocalHidden] = React.useState(new Set());
    const [draggedIndex, setDraggedIndex] = React.useState(null);

    // Initialize local state when dialog opens
    React.useEffect(() => {
        if (open) {
            const ordered = columnOrder.length > 0
                ? columnOrder.filter(name => columns.some(c => c.name === name))
                : columns.map(c => c.name);
            // Add any new columns not in the order
            columns.forEach(c => {
                if (!ordered.includes(c.name)) ordered.push(c.name);
            });
            setLocalOrder(ordered);
            setLocalHidden(new Set(hiddenColumns));
        }
    }, [open, columns, columnOrder, hiddenColumns]);

    const handleDragStart = (e, index) => {
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = "move";
    };

    const handleDragOver = (e, index) => {
        e.preventDefault();
        if (draggedIndex === null || draggedIndex === index) return;

        setLocalOrder(prev => {
            const newOrder = [...prev];
            const [dragged] = newOrder.splice(draggedIndex, 1);
            newOrder.splice(index, 0, dragged);
            setDraggedIndex(index);
            return newOrder;
        });
    };

    const handleDragEnd = () => {
        setDraggedIndex(null);
    };

    const handleToggleVisibility = (name) => {
        setLocalHidden(prev => {
            const next = new Set(prev);
            if (next.has(name)) {
                next.delete(name);
            } else {
                next.add(name);
            }
            return next;
        });
    };

    const handleSave = () => {
        // Apply visibility changes
        hiddenColumns.forEach(name => {
            if (!localHidden.has(name)) onToggleVisibility(name);
        });
        localHidden.forEach(name => {
            if (!hiddenColumns.has(name)) onToggleVisibility(name);
        });
        // Apply order changes
        onReorderColumns(localOrder);
        toast.success("Column settings saved");
        setOpen(false);
    };

    const orderedColumns = localOrder.map(name => columns.find(c => c.name === name)).filter(Boolean);

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button variant="outline" size="sm" className="flex-shrink-0">
                    <Columns className="w-4 h-4 md:mr-2" />
                    <span className="hidden md:inline">Organize</span>
                </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle>Organize Columns</DialogTitle>
                </DialogHeader>
                <div className="space-y-1 max-h-[400px] overflow-y-auto">
                    {orderedColumns.map((column, index) => (
                        <div
                            key={column.name}
                            draggable
                            onDragStart={(e) => handleDragStart(e, index)}
                            onDragOver={(e) => handleDragOver(e, index)}
                            onDragEnd={handleDragEnd}
                            className={`flex items-center gap-2 p-2 rounded hover:bg-muted/50 cursor-grab active:cursor-grabbing ${
                                draggedIndex === index ? "bg-muted opacity-50" : ""
                            }`}
                        >
                            <GripVertical className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                            <Checkbox
                                checked={!localHidden.has(column.name)}
                                onCheckedChange={() => handleToggleVisibility(column.name)}
                            />
                            <span className="flex-1 text-sm truncate">{column.name}</span>
                        </div>
                    ))}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => setOpen(false)}>
                        Cancel
                    </Button>
                    <Button onClick={handleSave}>
                        Save
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default React.memo(ColumnOrganizerDialog);
